import * as Y from 'yjs';
import { composeProjectedScene } from '../../../core/canvas/composeProjectedScene';
import { extractNodesAndEdgesFromDoc } from '../../../utils/snapshotToScene';
import { base64ToUint8Array, uint8ArrayToBase64 } from '../../../utils/yjs';
import { writeOrgSceneIntoExportDoc } from './orgProjectionExportMaterializer';
import { PROJECTED_LAYOUT_MAP, readProjectedLayout, writeProjectedPosition } from '../../../core/canvas/projectedLayout';

/** Rebuild, never clone: deleted CRDT values and unknown maps must not cross the boundary. */
export function planningPresentationSnapshot(source: Y.Doc): { update: string; stateVector: string } {
  const target = new Y.Doc();
  try {
    const annotations = composeProjectedScene(
      { version: 1, nodes: [], edges: [] }, extractNodesAndEdgesFromDoc(source),
    );
    writeOrgSceneIntoExportDoc(target, annotations);
    const layout = readProjectedLayout(source);
    if (layout) {
      target.getMap(PROJECTED_LAYOUT_MAP).set('mode', layout.mode);
      for (const [id, position] of layout.positions) writeProjectedPosition(target, id, position);
    }
    const raw = source.getMap('diagramSettings').get('settings');
    if (typeof raw === 'string') {
      const settings = JSON.parse(raw) as { orgchart?: Record<string, unknown> };
      const orgchart: Record<string, unknown> = {};
      for (const key of ['viewMode', 'personCardVariant', 'cardAccent', 'nodeSpacing', 'verticalSpacing']) {
        const value = settings.orgchart?.[key];
        if (typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value))) orgchart[key] = value;
      }
      target.getMap('diagramSettings').set('settings', JSON.stringify({ orgchart }));
    }
    return {
      update: uint8ArrayToBase64(Y.encodeStateAsUpdate(target)),
      stateVector: uint8ArrayToBase64(Y.encodeStateVector(target)),
    };
  } finally { target.destroy(); }
}

export function validatePlanningPresentationSnapshot(update: string): { update: string; stateVector: string } {
  const source = new Y.Doc();
  try {
    Y.applyUpdate(source, base64ToUint8Array(update));
    // Old clients embedded their delta in Yjs. Reject rather than silently
    // dropping a user's draft while reporting successful creation.
    if (source.getMap('scenarios').size > 0 || source.getMap('forkMetadata').size > 0) {
      throw new Error('Planning drafts must be submitted through the protected scenario boundary');
    }
    return planningPresentationSnapshot(source);
  } finally { source.destroy(); }
}
